import axios from 'axios';

// Create a custom axios instance with better configuration
const apiClient = axios.create({
  timeout: 15000,
  maxRedirects: 3,
  maxContentLength: 50 * 1024 * 1024, // 50MB
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle specific error types
    if (error.code === 'ERR_INSUFFICIENT_RESOURCES') {
      // console.warn('Server resources exhausted, retrying...');
      // You could implement retry logic here
    }
    
    if (error.code === 'ERR_NETWORK') {
      // console.warn('Network error, check connection...');
    }
    
    return Promise.reject(error);
  },
);

// Utility function for making API requests with retry logic
export const makeApiRequest = async (requestFn, maxRetries = 2, delay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      
      // Only retry on specific errors
      if (attempt < maxRetries && 
          (error.code === 'ERR_NETWORK' || 
           error.code === 'ERR_INSUFFICIENT_RESOURCES' ||
           error.response?.status >= 500)) {
        
        // console.log(`API request failed, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay * (attempt + 1)));
        continue;
      }
      
      // Don't retry for client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        break;
      }
    }
  }
  
  throw lastError;
};

/**
 * Get initials from a name
 * @param {string} name - The full name
 * @returns {string} The initials (max 2 characters)
 */
export const getInitials = (name) => {
  if (!name) return '?';
  
  const words = name.trim().split(' ').filter(word => word.length > 0);
  if (words.length === 0) return '?';
  
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  
  // Return first letter of first and last word
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a consistent color based on a string (name)
 * @param {string} str - The string to generate color from
 * @returns {string} A hex color code
 */
export const getColorFromString = (str) => {
  if (!str) return '#6366f1'; // Default indigo color
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate pleasant colors (avoiding too dark or too light)
  const colors = [
    '#6366f1', // Indigo
    '#8b5cf6', // Violet
    '#a855f7', // Purple
    '#d946ef', // Fuchsia
    '#ec4899', // Pink
    '#f43f5e', // Rose
    '#ef4444', // Red
    '#f97316', // Orange
    '#f59e0b', // Amber
    '#eab308', // Yellow
    '#84cc16', // Lime
    '#22c55e', // Green
    '#10b981', // Emerald
    '#14b8a6', // Teal
    '#06b6d4', // Cyan
    '#0ea5e9', // Sky
    '#3b82f6', // Blue
  ];
  
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Generate an avatar URL with initials using UI Avatars service
 * @param {string} name - The name to generate initials from
 * @param {number} size - The size of the avatar (default 128)
 * @returns {string} URL to the avatar image
 */
export const getInitialsAvatarUrl = (name, size = 128) => {
  const initials = getInitials(name);
  const bgColor = getColorFromString(name).replace('#', '');
  
  // Using UI Avatars service - free and reliable
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&size=${size}&background=${bgColor}&color=ffffff&bold=true&format=png`;
};

/**
 * Get the correct profile picture URL
 * Handles both local uploads (e.g., /uploads/users/...) and external URLs (e.g., Google profile pictures)
 * Falls back to initials avatar if no profile picture is available
 * @param {string} profilePicture - The profile picture path or URL
 * @param {string} baseUrl - The base URL for local uploads (e.g., api.img)
 * @param {string} defaultPic - Default picture to use if profilePicture is not available
 * @param {string} userName - User's name for generating initials avatar (optional)
 * @returns {string} The correct URL to display
 */
export const getProfilePictureUrl = (profilePicture, baseUrl, defaultPic = null, userName = null) => {
  if (!profilePicture) {
    // If userName is provided, generate initials avatar
    if (userName) {
      return getInitialsAvatarUrl(userName);
    }
    return defaultPic;
  }
  
  // If it's already a full URL (http:// or https://), use it directly
  if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
    return profilePicture;
  }
  
  // Normalize the path - ensure it starts with /
  const normalizedPath = profilePicture.startsWith('/') ? profilePicture : `/${profilePicture}`;
  
  // Ensure baseUrl doesn't end with / to avoid double slashes
  const cleanBaseUrl = baseUrl?.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  // Construct the full URL
  return `${cleanBaseUrl}${normalizedPath}`;
};

export default apiClient;
